import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import CallSheetHtmlPreview from "../components/CallSheetHtmlPreview";

// Prefer the local backend when the print page is loaded from localhost
// (that's how the backend's Playwright printer reaches us). Falls back to
// REACT_APP_BACKEND_URL for any manual browser access.
const API = (typeof window !== "undefined" && /^localhost(:|$)/.test(window.location.host))
  ? "http://localhost:8001/api"
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bare page rendered by the backend's Playwright printer.
 * Uses a short-lived `print_token` from the URL to auth against the API,
 * mounts the exact `<CallSheetHtmlPreview>` used in the editor at 1:1 scale
 * (no chrome, no controls), and flips a `data-print-ready` flag on <html>
 * once everything is loaded so Playwright can safely capture the page.
 */
export default function CallSheetPrintView() {
  const { callsheetId } = useParams();
  const [sp] = useSearchParams();
  const [cs, setCs] = useState(null);
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [err, setErr] = useState("");
  const marked = useRef(false);

  useEffect(() => {
    const token = sp.get("print_token") || "";
    const params = token ? { print_token: token } : {};
    (async () => {
      try {
        const { data: cData } = await axios.get(`${API}/callsheets/${callsheetId}`, { params, withCredentials: true });
        setCs(cData);
        // Project & crew are optional context — never block the print flow.
        const pReq = cData.project_id
          ? axios.get(`${API}/projects/${cData.project_id}`, { params, withCredentials: true }).then(r => r.data).catch(() => null)
          : Promise.resolve(null);
        const crewUrl = cData.project_id
          ? `${API}/projects/${cData.project_id}/crew`
          : `${API}/crew`;
        const crewReq = axios.get(crewUrl, { params, withCredentials: true }).then(r => r.data).catch(() => []);
        const [pData, crewData] = await Promise.all([pReq, crewReq]);
        setProject(pData);
        setCrew(crewData || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || "Failed to load call sheet");
      }
    })();
  }, [callsheetId, sp]);

  useEffect(() => {
    // Flip the ready flag as soon as either the CS is loaded OR the load errored,
    // so Playwright always releases and never hangs the request.
    if ((cs || err) && !marked.current) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        document.documentElement.setAttribute("data-print-ready", "1");
        marked.current = true;
      }));
    }
  }, [cs, err, project, crew]);

  if (err) {
    return <div style={{ padding: 40, fontFamily: "sans-serif", color: "#900" }}>Print load error: {err}</div>;
  }
  if (!cs) return null;

  return (
    <div style={{ background: "#fff", margin: 0, padding: 0 }} data-testid="print-root">
      <style>{`
        html, body { margin: 0; padding: 0; background: #fff; }
        /* Preserve colors, gradients, backgrounds and SVG strokes in the print PDF */
        html, body, * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        @page { size: Letter; margin: 0; }
        [data-print-page] { page-break-after: always; break-after: page; }
        [data-print-page]:last-of-type { page-break-after: auto; break-after: auto; }
      `}</style>
      <CallSheetHtmlPreview
        cs={cs}
        accentColor={project?.accent_color}
        logoDataUri={cs?.logo_data_uri || project?.logo_data_uri}
        logoAlign={project?.logo_align}
        logoMaxHeightPt={project?.logo_max_height_pt}
        isDraft={(cs?.status || "draft") === "draft" && sp.get("no_watermark") !== "1"}
        crewDb={crew}
        deptOrder={project?.departments || []}
        printMode={true}
      />
    </div>
  );
}
